import { validationLib } from "@agentcall/core/lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { Suspense, useEffect } from "react"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import * as z from "zod"
import { adminApi } from "@/api/client/admin"
import { useGetSession } from "@/api/client/auth/hooks"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { toast } from "@/hooks/use-toast"
import { useRefreshAdmin } from "@/store/session"

const profileFormSchema = z.object({
  name: z.string().min(1, "氏名を入力してください"),
  nameKana: validationLib.katakanaOptionalZ,
  department: z.string().optional(),
})

type ProfileFormValues = z.infer<typeof profileFormSchema>

export function ProfilePage() {
  const { data: sessionData, isLoading: isSessionLoading } = useGetSession()

  const userId = sessionData?.user?.id

  if (isSessionLoading || !userId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    )
  }

  return (
    <div>
      <ReactErrorBoundary>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-64">
              <Spinner className="size-8" />
            </div>
          }
        >
          <ProfileContent userId={userId} />
        </Suspense>
      </ReactErrorBoundary>
    </div>
  )
}

function ProfileContent({ userId }: { userId: string }) {
  const navigate = useNavigate()
  const { roleLabel } = usePermission()
  const refreshAdmin = useRefreshAdmin()

  const { data: adminData, refetch } = adminApi.useAdminGetSuspense(userId)

  const admin = adminData?.admin

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: "",
      nameKana: "",
      department: "",
    },
  })

  useEffect(() => {
    if (admin) {
      form.reset({
        name: admin.name || "",
        nameKana: admin.nameKana || "",
        department: admin.department || "",
      })
    }
  }, [admin, form])

  const updateAdminMutation = adminApi.useAdminUpdate({
    mutation: {
      onError: (error) =>
        toast({
          title: "プロフィールの更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })

  const onSubmit = (data: ProfileFormValues) => {
    if (!admin) return

    updateAdminMutation.mutate(
      {
        id: admin.id,
        data: {
          name: data.name,
          nameKana: data.nameKana || null,
          department: data.department || null,
          email: admin.email,
        },
      },
      {
        onSuccess: async () => {
          toast({
            title: "プロフィールを更新しました",
          })
          // adminデータを再取得
          await refetch()
          // jotai管理のadmin情報を更新（サイドバーも自動更新される）
          await refreshAdmin()
        },
      }
    )
  }

  const handleCancel = () => {
    if (admin) {
      form.reset({
        name: admin.name || "",
        nameKana: admin.nameKana || "",
        department: admin.department || "",
      })
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handlePasswordChange = () => {
    navigate("/profile/password")
  }

  if (!admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center justify-center p-1.5 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)] size-7 hover:bg-slate-50"
          >
            <ArrowLeft size={14} className="text-[#3f4652]" />
          </button>
          <h1 className="text-sm font-semibold text-[#3f4652]">プロフィール設定</h1>
        </div>
        <Button onClick={handlePasswordChange}>パスワードを変更する</Button>
      </div>

      {/* Form Card */}
      <div className="flex flex-col gap-4 w-full max-w-[895px]">
        <div className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
          {/* Card Header */}
          <div className="flex items-center gap-4 px-5 py-2.5 h-[52px] bg-white border-b border-slate-200 rounded-t-[10px]">
            <span className="text-sm font-semibold text-[#3f4652]">アカウント情報</span>
          </div>

          {/* Card Body */}
          <div className="bg-white p-5 rounded-b-[10px]">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                {/* 氏名 */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">氏名</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 氏名（カナ） */}
                <FormField
                  control={form.control}
                  name="nameKana"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">
                        氏名（カナ）
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 部署名 */}
                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">部署名</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* メールアドレス（読み取り専用） */}
                <div className="w-[560px]">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-[#3f4652]">メールアドレス</span>
                    <div className="h-8 px-3 py-2 flex items-center">
                      <span className="text-sm text-[#3f4652]">{admin.email}</span>
                    </div>
                  </div>
                </div>

                {/* 権限（読み取り専用） */}
                <div className="w-[560px]">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-[#3f4652]">権限</span>
                    <div className="h-8 px-3 py-2 flex items-center">
                      <span className="text-sm text-[#3f4652]">{roleLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - フォーム内に移動 */}
                <div className="flex items-center justify-end gap-4 mt-4">
                  <Button type="submit" loading={updateAdminMutation.isPending}>
                    保存する
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    キャンセル
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}
